import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils";
import { IntakeLookupControl } from "./intake-lookup";
import { useIntakeMessages } from "./intake-messages";
import { TicketIntakeCustomerLookupDialog } from "./ticket-intake-customer-lookup-dialog";
import { TicketIntakeCustomerSummary } from "./ticket-intake-customer-summary";
import { TicketIntakeSection } from "./ticket-intake-section";
import type { useTicketIntake } from "./use-ticket-intake";

export function TicketIntakeCustomerFields({ form, layout = "default" }: { form: ReturnType<typeof useTicketIntake>; layout?: "default" | "workbench" }) {
  const m = useIntakeMessages();
  const { session, values, busy, errors } = form;
  const isZamloan = form.projectCode?.toLowerCase() === "zamloan";
  const workbench = layout === "workbench";
  const searchLabel = isZamloan ? m.zamloanCustomerSearch : `${m.search} ${m.customer}`;
  const contactFields = workbench ? (["name", "phone"] as const) : (["name", "phone", "email"] as const);

  return (
    <TicketIntakeSection
      title={workbench ? m.customerDetails : m.customerInformation}
      description={
        workbench
          ? session?.form.schema.requiresCustomer
            ? m.memberRequiredHint
            : undefined
          : session?.form.schema.requiresCustomer
            ? m.customerRequiredHint
            : m.customerHint
      }
      action={workbench ? <TicketIntakeCustomerLookupDialog form={form} searchLabel={searchLabel} /> : undefined}
    >
      <FieldGroup>
        {!workbench ? (
          <Field data-invalid={Boolean(errors.customerId)}>
            <FieldLabel htmlFor="intake-customer-search" data-required={session?.form.schema.requiresCustomer ?? false}>
              {m.customer}
            </FieldLabel>
            <IntakeLookupControl
              key={`${session?.id}-customer`}
              capability="customers"
              inputId="intake-customer-search"
              searchLabel={searchLabel}
              invalid={Boolean(errors.customerId)}
              required={session?.form.schema.requiresCustomer ?? false}
              descriptionId={errors.customerId ? "intake-customer-error" : undefined}
              disabled={busy || !session}
              lookup={form.lookup}
              scope={session?.id ?? ""}
              value={values?.context.customerId}
              label={String(values?.context.customerIdLabel ?? "")}
              onChange={form.selectCustomer}
            />
            {errors.customerId ? <FieldError id="intake-customer-error">{errors.customerId}</FieldError> : null}
          </Field>
        ) : null}
        {!workbench && values?.context.orderId ? (
          <Field data-invalid={Boolean(errors.orderId)}>
            <FieldLabel htmlFor="intake-current-order">{m.order}</FieldLabel>
            <Input
              id="intake-current-order"
              readOnly
              disabled={busy}
              aria-invalid={Boolean(errors.orderId)}
              aria-describedby={errors.orderId ? "intake-current-order-error" : undefined}
              value={String(values?.context.orderIdLabel || values?.context.orderId)}
            />
            {errors.orderId ? <FieldError id="intake-current-order-error">{errors.orderId}</FieldError> : null}
          </Field>
        ) : null}
        <FieldGroup className={cn(workbench && "grid gap-5 @xl/intake:grid-cols-2")}>
          {contactFields.map((key) => (
            <Field key={key}>
              <FieldLabel htmlFor={`intake-contact-${key}`}>{m[key]}</FieldLabel>
              <Input
                id={`intake-contact-${key}`}
                disabled={busy}
                autoComplete="off"
                type={key === "email" ? "email" : key === "phone" ? "tel" : "text"}
                value={values?.requester?.[key] ?? ""}
                onChange={(event) => form.setRequester(key, event.target.value)}
              />
            </Field>
          ))}
        </FieldGroup>
        {workbench ? <TicketIntakeCustomerSummary form={form} /> : null}
        {!workbench ? <FieldDescription>{m.contactHint}</FieldDescription> : null}
      </FieldGroup>
    </TicketIntakeSection>
  );
}
